"use client"

import {
  Avatar,
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Input,
  Separator,
  Stack,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react"
import { Field } from "@chakra-ui/react/field"
import { signOut } from "next-auth/react"
import { useReducer } from "react"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { useForm } from "react-hook-form"

import { GoogleSignInButton } from "@/components/Auth/GoogleSignInButton"
import ErrorMessage from "@/components/ErrorMessage"
import { LogoutIcon } from "@/components/icons"
import { toaster } from "@/components/ui/toaster"
import { useAuth } from "@/contexts/AuthContext"
import { Comment } from "@/types/database"
import { sanitizeText } from "@/utils/sanitizeText"

interface CommentFormData {
  author_name: string
  author_email: string
  comment: string
}

interface CommentFormProps {
  postId: string
  parentId?: string | null
  onCommentSubmitted?: () => void
  onCancel?: () => void
  isReply?: boolean
  editingComment?: Comment
}

interface CommentSubmitState {
  isSubmitting: boolean
  submitMessage: { type: "success" | "error"; text: string } | null
}

type CommentSubmitAction =
  | { type: "start-submit" }
  | { type: "submit-success"; text: string }
  | { type: "submit-error"; text: string }

function commentSubmitReducer(
  state: CommentSubmitState,
  action: CommentSubmitAction
): CommentSubmitState {
  switch (action.type) {
    case "start-submit":
      return { isSubmitting: true, submitMessage: null }
    case "submit-success":
      return {
        isSubmitting: false,
        submitMessage: { text: action.text, type: "success" },
      }
    case "submit-error":
      return {
        isSubmitting: false,
        submitMessage: { text: action.text, type: "error" },
      }
    default:
      return state
  }
}

const CommentForm = ({
  editingComment,
  isReply = false,
  onCancel,
  onCommentSubmitted,
  parentId,
  postId,
}: CommentFormProps) => {
  const [{ isSubmitting, submitMessage }, dispatchSubmit] = useReducer(
    commentSubmitReducer,
    {
      isSubmitting: false,
      submitMessage: null,
    }
  )
  const { executeRecaptcha } = useGoogleReCaptcha()
  const { session } = useAuth()

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<CommentFormData>({
    values: editingComment
      ? {
          author_email: editingComment.author_email,
          author_name: editingComment.author_name,
          comment: editingComment.comment,
        }
      : session?.user
        ? {
            author_email: session.user.email || "",
            author_name: session.user.name || "",
            comment: "",
          }
        : undefined,
  })
  // console.log(session.user);
  const onSubmit = async (data: CommentFormData) => {
    // Sanitize all input fields
    const sanitizedData = {
      author_email: data.author_email.trim().toLowerCase(), // Email just needs trimming and lowercasing
      author_name: sanitizeText(data.author_name),
      comment: sanitizeText(data.comment),
    }

    if (!executeRecaptcha) {
      toaster.create({
        description: "Please wait a moment and try again.",
        duration: 3000,
        title: "reCAPTCHA not ready",
        type: "error",
      })
      return
    }

    // console.log('Setting isSubmitting to true');
    dispatchSubmit({ type: "start-submit" })

    try {
      // Execute reCAPTCHA with simpler timeout handling
      const recaptchaToken = await executeRecaptcha("submit_comment")

      const isEditing = !!editingComment
      const url = isEditing
        ? `/api/comments/${editingComment.id}`
        : "/api/comments"
      const method = isEditing ? "PUT" : "POST"

      const requestBody = {
        ...sanitizedData, // Use sanitized data
        avatar_url: session?.user?.image,
        is_authenticated: !!session,
        parent_id: parentId,
        post_id: postId,
        recaptcha_token: recaptchaToken,
        user_id: session?.user?.email || null, // Using email as user identifier
      }

      const response = await fetch(url, {
        body: JSON.stringify(requestBody),
        headers: {
          "Content-Type": "application/json",
        },
        method,
      })

      //console.log('Response status:', response.status);

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Unknown error" }))
        console.error("API Error:", response.status, errorData)
        dispatchSubmit({
          text: errorData.message || `Server error: ${response.status}`,
          type: "submit-error",
        })
        return
      }

      const result = await response.json()

      if (result.success) {
        dispatchSubmit({ text: result.message, type: "submit-success" })
        reset()
        onCommentSubmitted?.()
      } else {
        dispatchSubmit({ text: result.message, type: "submit-error" })
      }
    } catch (error) {
      console.error("Form submission error:", error)

      let errorMessage = "Failed to submit comment. Please try again."

      if (error instanceof Error) {
        if (
          error.name === "TimeoutError" ||
          error.message.includes("timeout")
        ) {
          errorMessage =
            "Request timed out. Please check your connection and try again."
        } else if (
          error.message.includes("NetworkError") ||
          error.message.includes("fetch")
        ) {
          errorMessage =
            "Network error. Please check your connection and try again."
        }
      }

      dispatchSubmit({
        text: errorMessage,
        type: "submit-error",
      })
    }
  }
  return (
    <>
      <Box px={8} py={8}>
        <Text
          fontFamily={"heading"}
          fontSize={isReply ? "lg" : "2xl"}
          fontWeight="bold"
        >
          {editingComment
            ? "Edit Response"
            : isReply
              ? "Reply to Response"
              : "Leave a Response"}
        </Text>
      </Box>

      {/* Authentication Section - Hide when editing */}
      {!editingComment && (
        <Box>
          <HStack align="center" justify="space-between">
            {session ? (
              <>
                <VStack
                  alignItems="flex-start"
                  borderTop={"1px solid {colors.border.muted}"}
                  width={"full"}
                >
                  {/* <Text color="fg.muted" fontSize={'sm'}>
                    Signed in as
                  </Text> */}
                  <HStack
                    alignItems="center"
                    bgColor="bg.subtle"
                    borderColor="border.muted"
                    borderStyle={"solid"}
                    borderWidth={1}
                    gap={3}
                    justifyContent={"center"}
                    px={2}
                    py={2}
                    rounded="0"
                  >
                    <Avatar.Root size={["sm", "md"]}>
                      <Avatar.Fallback name={session.user?.name || undefined} />
                      {session?.user?.image && (
                        <Avatar.Image src={session.user.image} />
                      )}
                    </Avatar.Root>
                    <VStack alignItems="flex-start" gap="0">
                      <Text fontSize={"sm"} fontWeight={"600"}>
                        {session.user?.name}
                      </Text>
                      <Text color="fg.muted" fontSize={"xs"}>
                        {session.user?.email?.substring(
                          0,
                          session.user.email?.indexOf("@") || 0
                        )}
                      </Text>
                    </VStack>
                    <VStack>
                      <IconButton
                        aria-label="Logout"
                        colorPalette={"gray"}
                        onClick={(e) => {
                          e.preventDefault()
                          signOut()
                        }}
                        p={2}
                        variant="surface"
                      >
                        <Icon>
                          <LogoutIcon />
                        </Icon>
                      </IconButton>
                    </VStack>
                  </HStack>
                </VStack>
              </>
            ) : (
              <VStack
                alignItems="center"
                borderTop={"1px solid {colors.border.muted}"}
                gap={6}
                width={"full"}
              >
                <VStack
                  alignItems="center"
                  bgColor="bg.subtle"
                  borderLeft={"1px solid {colors.border.muted}"}
                  borderRight={"1px solid {colors.border.muted}"}
                  justifyContent={"center"}
                  mx="auto"
                  py={6}
                  rounded="0"
                  width={"full"}
                >
                  <Text color="fg.muted" fontSize={"sm"}>
                    Sign in for a easier response
                  </Text>
                  <GoogleSignInButton />
                </VStack>
              </VStack>
            )}
          </HStack>
          <Separator borderColor={"border.muted"} />
        </Box>
      )}

      {submitMessage && (
        <HStack align="center" justify="center" px={8}>
          <ErrorMessage
            mb={4}
            message={submitMessage.text}
            status={submitMessage.type}
            title=""
          />
        </HStack>
      )}

      <VStack alignItems="stretch" px={8}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <VStack
            align="stretch"
            borderLeft={"1px solid {colors.border.muted}"}
            borderRight={"1px solid {colors.border.muted}"}
            gap={8}
            maxW="2xl"
            mx="auto"
            px={12}
            w="full"
          >
            <Stack flexDirection={{ base: "column", md: "row" }} gap={6} pt={8}>
              <Field.Root
                display={session ? "none" : "inherit"}
                invalid={!!errors.author_name}
                required
              >
                <Field.Label>
                  Name
                  <Field.RequiredIndicator />
                </Field.Label>
                <Input
                  {...register("author_name", {
                    minLength: {
                      message: "Name must be at least 2 characters",
                      value: 2,
                    },
                    required: "Name is required",
                  })}
                  _dark={{
                    _focus: {
                      boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.25)",
                    },
                    bg: "gray.900",
                    borderColor: "gray.700",
                  }}
                  _focus={{
                    borderColor: "primary.300",
                    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                  }}
                  bg="white"
                  borderColor="gray.200"
                  borderRadius="sm"
                  disabled={!!session && !editingComment}
                  fontSize={{ base: "md", sm: "sm" }}
                  placeholder="Your name"
                />
                <Field.ErrorText>{errors.author_name?.message}</Field.ErrorText>
              </Field.Root>

              <Field.Root
                display={session ? "none" : "inherit"}
                invalid={!!errors.author_email}
                required
              >
                <Field.Label>
                  Email
                  <Field.RequiredIndicator />
                </Field.Label>
                <Input
                  type="email"
                  {...register("author_email", {
                    pattern: {
                      message: "Please enter a valid email address",
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    },
                    required: "Email is required",
                  })}
                  _dark={{
                    _focus: {
                      boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.25)",
                    },
                    bg: "gray.900",
                    borderColor: "gray.700",
                  }}
                  _focus={{
                    borderColor: "primary.300",
                    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                  }}
                  bg="white"
                  borderColor="gray.200"
                  borderRadius="sm"
                  disabled={!!session && !editingComment}
                  fontSize={{ base: "md", sm: "sm" }}
                  placeholder="your.email@example.com"
                />
                <Field.HelperText>
                  {session
                    ? "Using your Google account email"
                    : "Your email will not be published"}
                </Field.HelperText>
                <Field.ErrorText>
                  {errors.author_email?.message}
                </Field.ErrorText>
              </Field.Root>
            </Stack>
            <Field.Root invalid={!!errors.comment} required>
              <Field.Label>
                Response
                <Field.RequiredIndicator />
              </Field.Label>
              <Textarea
                {...register("comment", {
                  maxLength: {
                    message: "Response must be less than 1000 characters",
                    value: 1000,
                  },
                  minLength: {
                    message: "Response must be at least 10 characters",
                    value: 10,
                  },
                  required: "Response is required",
                })}
                _dark={{
                  _focus: {
                    boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.25)",
                  },
                  bg: "gray.900",
                  borderColor: "gray.700",
                }}
                _focus={{
                  borderColor: "primary.300",
                  boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                }}
                bg="white"
                borderColor="gray.200"
                borderRadius="sm"
                fontSize={{ base: "md", sm: "sm" }}
                placeholder="Share your thoughts..."
                resize="vertical"
                rows={6}
              />
              <Field.ErrorText>{errors.comment?.message}</Field.ErrorText>
            </Field.Root>

            <HStack gap={3} pb={8}>
              <Button
                colorPalette="primary"
                loading={isSubmitting}
                loadingText={editingComment ? "Updating..." : "Submitting..."}
                size={isReply ? "sm" : "sm"}
                type="submit"
              >
                {editingComment
                  ? "Update Response"
                  : isReply
                    ? "Submit Reply"
                    : "Submit Response"}
              </Button>
              {(isReply || editingComment) && onCancel && (
                <Button onClick={onCancel} size="sm" variant="outline">
                  Cancel
                </Button>
              )}
            </HStack>
          </VStack>
        </form>
      </VStack>
    </>
  )
}

export default CommentForm
